import { Link } from "react-router-dom";
import { Compass, ShieldCheck, ArrowRight, UserRound } from "lucide-react";
import ListenButton from "../components/ListenButton";
import UnsplashHero from "../components/UnsplashHero";

/* "My Travel Hub — What & Why?" — owner-supplied long-form article (Feb 2026). */

const S = ({ h, children }) => (
  <section className="mb-10">
    <h2 className="font-heading font-900 text-2xl text-zinc-900 mb-3">{h}</h2>
    {children}
  </section>
);
const P = ({ children }) => <p className="text-base text-zinc-700 mb-3 leading-relaxed">{children}</p>;
const UL = ({ items, cols = 2 }) => (
  <ul className={`grid gap-x-6 gap-y-1 mb-3 text-sm text-zinc-700 list-disc ml-5 ${cols === 3 ? "sm:grid-cols-2 lg:grid-cols-3" : cols === 2 ? "sm:grid-cols-2" : ""}`}>
    {items.map((i) => <li key={i}>{i}</li>)}
  </ul>
);
const H3 = ({ children }) => <h3 className="font-heading font-800 text-lg text-zinc-900 mt-5 mb-2">{children}</h3>;

export default function MyTravelHubGuide() {
  return (
    <div className="min-h-screen bg-white" data-testid="mth-guide-page">
      <UnsplashHero
        query="traveler notebook luggage boarding pass"
        eyebrow="TravelerPulse Guide"
        title="My Travel Hub — Your Private, Secure Home for Every Journey"
        subtitle="Plan it. Store it. Organize it. Remember it. Travel with confidence."
        testid="mth-guide-hero"
      />
      {/* header */}
      <div className="border-b border-zinc-200 bg-gradient-to-b from-blue-50/70 to-white">
        <div className="max-w-3xl mx-auto px-5 py-8">
          <div className="text-xs font-800 uppercase tracking-widest text-blue-700 mb-3 flex items-center gap-1.5">
            <Compass className="w-4 h-4" /> TravelerPulse Guide
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/travel-hub" data-testid="mth-guide-open-hub"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-700 text-white text-sm font-700 rounded-full hover:bg-blue-800 transition-colors">
              <UserRound className="w-4 h-4" /> Open My Travel Hub <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/security-privacy" data-testid="mth-guide-security-link"
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-zinc-300 text-zinc-800 text-sm font-700 rounded-full hover:border-blue-500 hover:text-blue-700 transition-colors">
              <ShieldCheck className="w-4 h-4" /> How we protect your data
            </Link>
          </div>
        </div>
      </div>

      <article id="mth-guide-readable" className="max-w-3xl mx-auto px-5 py-12">
        <div className="-mt-2 mb-6"><ListenButton targetId="mth-guide-readable" label="Listen to this guide" /></div>
        <P>Modern travel can involve dozens of moving parts: passports, visas, airline reservations, hotels, trains,
          rental cars, confirmations, insurance policies, loyalty programs, expenses, documents, reminders, arrival
          forms and emergency information.</P>
        <P>Important information often ends up scattered across emails, screenshots, apps, paper documents and
          messaging conversations.</P>
        <P><b>TravelerPulse My Travel Hub</b> is designed to bring those pieces together into one organized personal
          travel workspace. Instead of searching through emails at an airport or wondering where you saved a
          confirmation number, My Travel Hub gives travelers a central place to organize the information they may
          need before, during and after a journey.</P>

        <S h="One Trip. One Hub.">
          <P>Every journey can have its own Trip Hub containing the people, reservations, documents, notes and
            reminders associated with that trip. A traveler could create <b>Japan — March 2027</b> and organize within it:</P>
          <UL cols={3} items={["Travelers", "Flights", "Airports", "Hotels and other stays", "Trains and rail passes",
            "Buses and transfers", "Rental cars", "Taxis and rideshare information", "Cruises", "Tours and activities",
            "Passports", "Visas", "Arrival cards", "Travel insurance", "Health/travel documents", "Reservations",
            "Expenses", "Emergency contacts", "Notes", "Photos and documents", "Useful links", "Reminders"]} />
          <P>The objective is simple: <b>when you need something during your trip, you should know exactly where to find it.</b></P>
        </S>

        <S h="Manage Everyone Traveling With You">
          <P>Travel becomes more complicated when couples, families or groups travel together. My Travel Hub
            maintains individual traveler profiles within a trip. Each passenger can have their own:</P>
          <UL cols={3} items={["Full name", "Passport information", "Visa information", "Flight information",
            "Seat information", "Loyalty numbers", "Travel documents", "Insurance information", "Emergency information"]} />
          <P>This makes the Hub useful for solo travelers as well as couples, families and groups.</P>
        </S>

        <S h="Flights & Airports">
          <P>Store important flight information such as:</P>
          <UL cols={3} items={["Airline", "Flight number", "Confirmation/booking number", "Departure airport",
            "Arrival airport", "Terminal", "Gate", "Departure date and time", "Arrival date and time",
            "Seat assignment", "Baggage information", "Airline contact information", "Airline website",
            "Boarding passes", "Reservation documents", "Notes"]} />
          <P>TravelerPulse also connects travelers with the broader information available throughout the platform,
            including airport information, terminals, lounges, restaurants, shops, transportation and other services.</P>
        </S>

        <S h="Hotels & Accommodations">
          <P>Keep accommodation information together with the trip. Store:</P>
          <UL cols={3} items={["Hotel or property name", "Address", "Phone number", "Website", "Confirmation number",
            "Check-in date/time", "Check-out date/time", "Room information", "Rate", "Amount paid", "Balance due",
            "Cancellation deadline", "Reservation document", "Special instructions", "Notes"]} />
          <P>Vacation rentals, resorts, hostels, timeshares and other accommodations can be handled in the same area.</P>
        </S>

        <S h="Ground Transportation">
          <P>A journey doesn't end when the airplane lands. My Travel Hub organizes:</P>
          <UL cols={3} items={["Rental cars", "Trains", "Buses", "Airport transfers", "Taxis", "Rideshare",
            "Private transportation", "Parking"]} />
          <P>Save reservation numbers, pickup locations, times, addresses, costs, contact information and supporting documents.</P>
        </S>

        <S h="Passport Information">
          <P>International travelers frequently need passport information when completing airline, hotel, visa and
            immigration forms. My Travel Hub provides a protected location for storing relevant passport details for
            each traveler, such as:</P>
          <UL cols={3} items={["Passport holder", "Passport number", "Country of issue", "Date of issue",
            "Expiration date", "Place of issue", "Uploaded passport copy"]} />
          <P>Expiration reminders help travelers identify passports that may need renewal before a future journey.</P>
        </S>

        <S h="Visa Management">
          <P>Visa requirements can be one of the most confusing parts of international travel. My Travel Hub
            maintains visa information separately for every passenger, including traveler name, country being
            visited, required travel date and visa status:</P>
          <UL cols={3} items={["Not started", "Required", "Not required", "Applied", "Pending", "Approved", "Received"]} />
          <P>Visa categories (Tourist/Visitor, Business, Schengen, Transit, Other) and formats (electronic visa,
            visa in passport, other) are supported. Travelers can also record:</P>
          <UL cols={3} items={["Application date", "Approval date", "Visa number", "Validity period",
            "Expiration date", "Notes", "Uploaded copy"]} />
          <P>Where available, TravelerPulse links to the appropriate <b>official government visa website</b>, helping
            users avoid questionable third-party visa sites.</P>
        </S>

        <S h="Arrival & Entry Cards">
          <P>Many countries now require travelers to complete electronic arrival, immigration, health or customs
            declarations before arrival. My Travel Hub helps travelers keep track of them. For each traveler:</P>
          <UL items={["Arrival card required?", "Submitted? — Yes/No", "Submission date",
            "Confirmation/reference number", "Upload confirmation", "Official arrival-card website"]} />
          <P>Reminders are especially useful when a country only allows an arrival form to be submitted within a
            certain number of days before arrival.</P>
        </S>

        <S h="Travel & Health Documents">
          <P>Where appropriate, travelers may maintain information about documents required for a destination, including:</P>
          <UL items={["Travel health cards", "Vaccination documentation", "Travel insurance cards",
            "Emergency assistance information", "Other destination-required documents"]} />
          <P>Documents can be attached directly to the appropriate trip or traveler record.</P>
        </S>

        <S h="Travel Insurance">
          <P>Keep insurance information available when it is most important. Store:</P>
          <UL cols={3} items={["Insurance company", "Policy number", "Travelers covered", "Coverage dates",
            "Emergency telephone number", "Claims contact information", "Policy document", "Notes"]} />
        </S>

        <S h="Upload Important Travel Documents">
          <P>My Travel Hub provides upload options throughout the system rather than forcing travelers to maintain a
            separate document library. Examples include:</P>
          <UL cols={3} items={["Passport copy", "Visa", "Boarding pass", "Airline itinerary", "Hotel confirmation",
            "Rail ticket", "Rental-car confirmation", "Insurance policy", "Arrival-card confirmation",
            "Tour reservation", "Receipts", "Other travel documents"]} />
          <P>Documents remain associated with the appropriate trip, traveler or reservation.</P>
        </S>

        <S h="Reminders — Don't Forget the Important Things">
          <P>Many travel requirements are time-sensitive. Create reminders with a date and time for virtually any
            Hub item. Examples:</P>
          <UL items={["Passport expires in six months", "Apply for Japan visa", "Complete arrival card",
            "Online check-in opens tomorrow", "Hotel cancellation deadline", "Reserve airport transportation",
            "Return rental car", "Travel insurance payment due", "Check in for flight"]} />
          <P>Reminders are delivered through TravelerPulse notification channels, including in-app and email notifications.</P>
        </S>

        <S h="Active, Archived & Reminder Views">
          <P>Travel information shouldn't disappear simply because a trip is finished. My Travel Hub organizes
            information into convenient views:</P>
          <UL cols={1} items={["Active — current and upcoming trips and records",
            "Archived — completed trips and information the traveler wants to retain",
            "Reminders — upcoming tasks and deadlines across trips"]} />
          <P>Instead of permanently deleting useful travel history, users can archive trips for future reference.</P>
        </S>

        <S h="Trip Expenses">
          <P>Travelers can maintain a simple record of what a journey costs. Categories include:</P>
          <UL cols={3} items={["Airfare", "Hotels", "Rail", "Rental cars", "Taxis and rideshare", "Restaurants",
            "Tours", "Attractions", "Insurance", "Visas", "Shopping", "Tips", "Other expenses"]} />
          <P>This provides travelers with a useful overview of estimated versus actual trip expenses.</P>
        </S>

        <S h="Notes, Links & Travel Information">
          <P>Not everything about a journey is a reservation. Travelers discover restaurants, attractions, videos,
            articles, maps and recommendations while planning. My Travel Hub provides places to save:</P>
          <UL cols={3} items={["Notes", "Travel tips", "Food tips", "Useful websites", "Videos", "Podcasts",
            "Maps", "Warnings", "Photos", "Other information"]} />
          <P>The Hub therefore becomes more than document storage — it becomes a personal travel notebook.</P>
        </S>

        <S h="TravelerPulse Information Throughout Your Journey">
          <P>My Travel Hub works together with the wider TravelerPulse platform. While planning or traveling, users
            can access information about airlines, airports, lounges, restaurants, airport shops, hotels, ground
            transportation, trains, buses, rental cars, rideshare, destinations, travel tips, deals and travel news.</P>
          <P>This creates an important distinction: <b>My Travel Hub organizes your trip. TravelerPulse helps you
            explore the world around your trip.</b></P>
        </S>

        <S h="Ask a Question — Search">
          <P>TravelerPulse's Ask a Question — Search feature gives travelers another layer of assistance. Instead of
            navigating through numerous menus, travelers can ask questions such as:</P>
          <UL items={['"Where is my hotel?"', '"What terminal does my airline use?"', '"Do I need a visa?"',
            '"Where are the lounges at this airport?"', '"What restaurants are in Terminal 2?"',
            '"What documents should I prepare for this trip?"']} />
          <P>AI-assisted answers combine TravelerPulse information with appropriate external information sources
            while directing travelers to authoritative sources when verification is important.</P>
        </S>

        <S h="Privacy and Security Are Central to My Travel Hub">
          <P>A travel organizer can contain highly sensitive personal information. Passport details, visas,
            itineraries and uploaded documents require substantially greater protection than ordinary travel
            articles or public discussion posts. For that reason, security is treated as a core architectural
            requirement, rather than an optional feature added later.</P>

          <H3>Private by Default</H3>
          <P>Information placed in My Travel Hub is treated as private unless the traveler deliberately chooses to
            share it. Personal travel documents never automatically become part of TravelerPulse's public community areas.</P>

          <H3>Encryption</H3>
          <P>Sensitive information is protected using modern encryption practices — while being transmitted
            (encrypted connections such as HTTPS/TLS protect information traveling between your device and
            TravelerPulse) and while being stored (sensitive databases, uploaded documents and backups use
            appropriate encryption at rest). Encryption reduces the risk that readable personal information could be
            exposed if infrastructure or storage were improperly accessed.</P>

          <H3>Strong Account Protection</H3>
          <P>TravelerPulse employs strong authentication and session-security measures appropriate to the
            sensitivity of My Travel Hub, and additional verification is required for particularly sensitive operations:</P>
          <UL cols={3} items={["Secure authentication", "One-time verification codes", "Multi-factor authentication",
            "Session expiration", "Device/session management", "Login monitoring", "Rate limiting",
            "Suspicious-activity detection"]} />

          <H3>Extra Protection for Sensitive Documents</H3>
          <P>Passport copies, visas and other identity documents deserve stronger controls than ordinary uploaded
            photographs. The system separates and protects these documents using measures such as:</P>
          <UL cols={3} items={["Private storage", "Restricted access permissions", "Time-limited secure document links",
            "Encryption", "Access logging", "File-type validation", "Malware scanning",
            "Protection against unauthorized public indexing"]} />
          <P>A passport upload never has an ordinary publicly accessible file URL.</P>

          <H3>Access Control for Couples and Families</H3>
          <P>Travelers may want to share a trip without sharing every piece of personal information. My Travel Hub
            supports permission-based sharing. A trip owner might allow another family member to view the itinerary
            — but not view passport documents. Or allow a spouse to view and edit the entire trip. Permissions are
            controlled by the person who owns or manages the information.</P>

          <H3>Protecting Information From AI</H3>
          <P>AI can make travel planning substantially easier, but sensitive personal information is handled
            carefully. TravelerPulse follows an important principle: <b>give AI only the minimum information
            necessary to answer the traveler's question.</b> An AI system answering "What restaurants are near my
            hotel?" does not need your passport number. This concept of data minimization extends throughout My Travel Hub.</P>

          <H3>Official Sources for Critical Travel Requirements</H3>
          <P>Visa, passport, immigration, customs and health requirements can change. TravelerPulse distinguishes
            between general travel guidance and authoritative information, linking travelers directly to government
            immigration agencies, embassies and consulates, official visa portals, official arrival-card systems,
            airlines, airports and other authoritative organizations. Always verify critical entry requirements with
            official authorities before traveling.</P>

          <H3>Security Monitoring & Audit Trails</H3>
          <P>Behind the scenes, important security events are logged and monitored — sensitive-document access,
            document uploads, permission changes, trip-sharing activity, authentication changes and sensitive-data
            deletion. Audit trails help detect unusual activity and investigate potential security incidents.</P>

          <H3>Backups and Reliability</H3>
          <P>Security also means making sure travelers can access their information when they need it: secure
            backups, database redundancy, recovery procedures, system monitoring, software security updates,
            vulnerability management and incident-response procedures. Travel information can be especially
            important when someone is thousands of miles from home.</P>

          <H3>Travelers Remain in Control</H3>
          <UL cols={3} items={["View their information", "Edit incorrect information", "Archive trips",
            "Delete information", "Remove uploaded documents", "Manage sharing permissions",
            "Review active sessions", "Close their account"]} />
          <P>TravelerPulse collects and retains only information reasonably necessary to provide the requested
            services and meet applicable legal or operational requirements.</P>
        </S>

        <S h="A Personal Travel Command Center">
          <P>My Travel Hub is ultimately about reducing one of the most frustrating parts of travel:
            <b> information scattered everywhere.</b></P>
          <P>Instead of looking through email, text messages, screenshots, airline apps, hotel apps and folders,
            imagine opening one place and seeing your trip, your travelers, your itinerary, your flights, your
            stays, your transportation, your passports, your visas, your documents, your reminders, your expenses
            and your notes — all organized around the journey.</P>
          <div className="border border-blue-200 bg-blue-50/60 p-5 my-5 text-center">
            <div className="font-heading font-900 text-xl text-blue-800">TravelerPulse My Travel Hub</div>
            <p className="text-sm text-zinc-700 mt-1">Everything about your journey. Organized in one place.
              Protected with care. Available when you need it.</p>
            <p className="font-800 text-sm text-blue-700 mt-2 tracking-wide">EXPLORE. PLAN. ORGANIZE. SHARE. TRAVEL.</p>
            <Link to="/travel-hub" data-testid="mth-guide-cta"
              className="mt-4 inline-flex items-center gap-2 px-6 py-2.5 bg-blue-700 text-white text-sm font-700 rounded-full hover:bg-blue-800 transition-colors">
              Open My Travel Hub <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </S>

        <p className="text-xs text-muted-foreground border-t border-zinc-200 pt-4">
          Security note: TravelerPulse describes specific safeguards publicly only after those controls have been
          implemented and verified — see <Link to="/security-privacy" className="text-blue-700 hover:underline">Security
          &amp; Privacy at My Travel Hub</Link> for what is in place today. Security claims reflect the production
          system rather than planned capabilities.
        </p>
      </article>
    </div>
  );
}
